<template>
    <div class="ls-flow--container">
        <LsTemplatedLoading :show="loading" :full-screen="true" test-id="loading" />
        <div v-if="!loading" test-id="form_wrapper">
            <div class="ls-flow--title">Confirm Change Frequency</div>

            <form novalidate class="ls-flow--form" @submit.prevent="createScheduleCycleChange">
                <LsRadios
                    v-model="selectedFrequency"
                    track-id="change_frequency_view-options"
                    name="cycle"
                    label="Frequency"
                    :options="frequencyOptions"
                />

                <div>
                    <p>Next service scheduled on</p>
                    <p class="ls-flow--date-range">{{ range }}</p>
                </div>

                <LsInput
                    v-model="zendesk_ticket_id"
                    track-id="change_frequency_view-zendesk_ticket_id"
                    label="Zendesk ticket number"
                    name="zendesk_ticket_id"
                    type="number"
                    placeholder="Enter number"
                    :validation="{ required: true, min_value: 1000000, max: 8 }"
                />

                <LsError v-if="isInvalid" />

                <LsButton
                    track-id="change_frequency_view-confirm_frequency_change"
                    text="Confirm Change Frequency"
                    :loading="isUpdating"
                    type="submit"
                />
            </form>
        </div>
    </div>
</template>

<script>
import _ from 'lodash';
import { lsErrorService, lsFilters, lsModalService } from 'ls-vue-common';
import gql from 'graphql-tag';
import { lsScheduleCycles } from '@/constants';

import { lsHelpers } from '@/helpers';
import ScheduleCycleChange from '@/graphql/queries/ScheduleCycleChange.gql';
import Schedule from '@/graphql/queries/Schedule.gql';
import Zip from '@/graphql/queries/Zip.gql';
import CreateScheduleCycleChange from '@/graphql/mutations/CreateScheduleCycleChange.gql';
import { LsButton, LsRadios, LsInput } from '@/components/common';
import { FlowSuccessModal } from '@/modals';
import LsTemplatedLoading from '@/components/LsTemplatedLoading/LsTemplatedLoading.vue';
import { apolloClient } from '@/vue-apollo';
import LsError from '@/components/common/LsError/LsError.vue';

export default {
    name: 'ChangeFrequencyView',
    components: {
        LsButton,
        LsRadios,
        LsInput,
        LsTemplatedLoading,
        LsError,
    },
    $_veeValidate: {
        validator: 'new',
    },
    data() {
        return {
            loading: 0,
            isUpdating: false,
            isInvalid: false,

            options: [],
            isMonthlyMowingDisabled: false,
            property: apolloClient.readFragment({
                id: `Property:${this.$route.params.property_id}`,
                fragment: gql`
                    fragment property on Property {
                        street
                        zip
                    }
                `,
            }),
            schedule: apolloClient.readFragment({
                id: `Schedule:${this.$route.params.schedule_id}`,
                fragment: gql`
                    fragment schedule on Schedule {
                        cycle
                    }
                `,
            }),

            // form data
            selectedFrequency: null,
            immediate: true,
            zendesk_ticket_id: '',
        };
    },
    apollo: {
        scheduleCycleChange: {
            query: ScheduleCycleChange,
            variables() {
                return {
                    customer_id: _.toNumber(this.$route.params.customer_id),
                    property_id: _.toNumber(this.$route.params.property_id),
                    schedule_id: _.toNumber(this.$route.params.schedule_id),
                };
            },
            result({ data, loading }) {
                if (!loading) {
                    this.options = data.scheduleCycleChange;
                    this.selectedFrequency = this.frequencyOptions[0].value;
                }
            },
            error(error) {
                lsErrorService.log({ error, showAlert: true });
                this.$apollo.queries.scheduleCycleChange.skip = true;
                lsHelpers.goToParentView({ replace: true });
            },
            loadingKey: 'loading',
            // "no-cache" would be better here - but the loading state was being wonky when I tried that
            fetchPolicy: 'network-only',
        },
        isMonthlyMowingDisabled: {
            query: Zip,
            client: 'lighthouse',
            variables() {
                return { zip: this.normalizedZip };
            },
            update({ getZip }) {
                const market = getZip ? getZip.market : null;
                const activeSeason = market ? market.activeSeason : null;
                return lsHelpers.isMonthlyMowingDisabled(activeSeason);
            },
            skip() {
                return !this.normalizedZip;
            },
            fetchPolicy: 'network-only',
        },
    },
    computed: {
        range() {
            const {
                immediate_customer_starts_at,
                immediate_customer_ends_at,
            } = this.selectedFrequency;

            return lsHelpers.dateRange({
                start: immediate_customer_starts_at,
                end: immediate_customer_ends_at,
            });
        },

        normalizedZip() {
            const zip = _.get(this.property, 'zip');
            return zip ? String(zip).slice(0, 5) : null;
        },

        frequencyOptions() {
            let hasMonthlyMowing = false;

            let frequencyOptions = _.map(this.options, (option) => {
                let isOptionDisabled = false;

                if (option.cycle === lsScheduleCycles.monthly) {
                    hasMonthlyMowing = true;
                    isOptionDisabled = this.isMonthlyMowingDisabled;
                }

                return {
                    value: {
                        cycle: option.cycle,
                        customer_starts_at: option.customer_starts_at,
                        customer_ends_at: option.customer_ends_at,
                        immediate_available: option.immediate_available,
                        immediate_customer_starts_at: option.immediate_customer_starts_at,
                        immediate_customer_ends_at: option.immediate_customer_ends_at,
                    },
                    label: `${lsFilters.frequency({
                        cycle: option.cycle,
                    })} - ${lsFilters.currency({
                        amount: option.total,
                    })}`,
                    subLabel: lsHelpers.pricingSubLabel(option),
                    disabled: isOptionDisabled,
                };
            });

            if (!hasMonthlyMowing) {
                frequencyOptions.push({ value: null, disabled: true, label: 'Monthly (N/A)' });
            }

            return frequencyOptions;
        },
    },
    methods: {
        async createScheduleCycleChange() {
            const valid = await this.$validator.validateAll();
            this.isInvalid = !valid;
            if (!valid) {
                return;
            }

            this.isUpdating = true;
            this.$apollo
                .mutate({
                    mutation: CreateScheduleCycleChange,
                    variables: {
                        customer_id: _.toNumber(this.$route.params.customer_id),
                        property_id: _.toNumber(this.$route.params.property_id),
                        schedule_id: _.toNumber(this.$route.params.schedule_id),
                        new_cycle: this.selectedFrequency.cycle,
                        immediate: this.immediate,
                        zendesk_ticket_id: this.zendesk_ticket_id
                            ? _.toInteger(this.zendesk_ticket_id)
                            : null,
                    },
                    refetchQueries: [
                        {
                            query: Schedule,
                            variables: {
                                customer_id: _.toNumber(this.$route.params.customer_id),
                                property_id: _.toNumber(this.$route.params.property_id),
                                schedule_id: _.toNumber(this.$route.params.schedule_id),
                            },
                        },
                    ],
                })
                .then(({ data }) => {
                    lsModalService.open({
                        parent: this,
                        component: FlowSuccessModal,
                        props: {
                            title: `Frequency changed for ${this.property.street}`,
                            copy: data.createScheduleCycleChange.copy,
                        },
                    });
                })
                .catch((error) => {
                    lsErrorService.log({ error, showAlert: true });
                })
                .finally(() => {
                    this.isUpdating = false;
                });
        },
    },
};
</script>
